/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { makeStyles } from 'utils/makeStylesAdapter';
import {
  Grid,
  Box,
  TextareaAutosize,
  Typography,
  IconButton,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  CurrencyTextField,
  InputConsensus,
  ButtonConsensus,
  KeyboardDatePicker,
  ModalConfirm,
  InstallmentTypeRadioGroup,
  InstallmentFixedPerPeriod,
  InstallmentVariablePerPeriod,
} from 'components';
import { colors } from 'utils/colorsSteps';
import { Skeleton } from '@mui/material';
import { toast } from 'react-toastify';
import { formatDateToISO8601 } from 'utils/date-time.utils';
import associationService from 'services/association/associationService';
import CircularProgress from '@mui/material/CircularProgress';
import moment from 'moment';
import { currencyFormat, getOnlyNumbers } from 'utils/currency';
import { parcelsSum, validateParcels } from 'utils/parcels';
import { PARTNER_SITUATIONS } from 'constants/partner-situations';
import { useCustomerConfig } from 'hooks/useCustomerConfig';
import agreementService from 'services/agreement/agreementService';
import { INSTALLMENT_TYPES } from 'constants/agreement/installmentTypes';
import InstallmentPaidBySpecificDate from 'components/InstallmentPaidBySpecificDate';
import { PENALTY_KINDS } from 'constants/agreement/penaltyKinds';
import projectService from 'services/project/projectService';
import LazyAutocomplete from 'components/LazyAutocomplete';
import { AutoCompleteConfig } from 'constants/autocomplete';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import { PARAMETER_KEYS } from 'constants/parameters/parameter-keys';
import { useSelector } from 'react-redux';
import { useParameterKeys } from 'hooks/useParameterKeys';

const useStyles = makeStyles(theme => ({
  containerForm: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    margin: theme.spacing(2, 0, 2, 0),
  },
  containerParcels: {
    padding: theme.spacing(2),
    backgroundColor: '#ececec',
    borderRadius: '0.7rem',
    margin: theme.spacing(0, 0.8, 1, 0.8),
  },
}));

const PecuniaryPenalties = props => {
  const {
    setDisabledActionsPenaltyTable = {},
    selectedIndex = null,
    setSelectedIndex = {},
    pecuniary,
    setPecuniary,
    description,
    setDescription,
    institutionType,
    institutionSelected,
    judicialDistrictId,
    appliedInstituteFilterKey = null,
  } = props;
  const classes = useStyles();

  const context = 'PECUNIARY';

  const { isMPDFT, enablePenaltyTypes } = useParameterKeys();

  const parameters = useSelector(state => state.parameters);

  const enableInstallmentTypes = parameters.find(
    param => param.key === PARAMETER_KEYS.CONFIG_ENABLE_INSTALLMENT_TYPES && param.value === 'Sim',
  );

  const [row, setRow] = useState(null);

  const [installmentType, setInstallmentType] = useState(
    row?.installmentType || INSTALLMENT_TYPES.PECUNIARY.FIXED_PER_PERIOD,
  );

  const [openDeleteParcelModalConfirm, setOpenDeleteParcelModalConfirm] = useState(false);
  const [parcelToDelete, setParcelToDelete] = useState(null);

  const initialValues = {
    paymentDeadline: null,
    value: 0,
    penalty: PENALTY_KINDS.PECUNIARY,
    description: isMPDFT ? null : '',
    descriptionLocal: isMPDFT ? '' : description,
    association: null,
    parcels: [],
    project: null,
    numberOfParcels: 1,
    type: null,
    periodicy: '',
    totalValue: 0,
    minimumValue: null,
    maximumValue: null,
    initialDate: null,
  };

  const [formState, setFormState] = useState({
    isValid: false,
    values: { ...initialValues },
    touched: {},
    errors: {},
  });

  const [isAddingParcel, setIsAddingParcel] = useState(false);
  const [newParcel, setNewParcel] = useState({
    paymentDeadline: null,
    value: 0,
    minimumValue: null,
    maximumValue: null,
  });

  const handleToggleAddParcel = () => {
    setIsAddingParcel(!isAddingParcel);
    setNewParcel({ inicialDate: '', hours: '' });
  };

  const handleAddNewParcel = () => {
    const { paymentDeadline, value, maximumValue, minimumValue } = newParcel;

    if (!paymentDeadline) {
      return toast.warn('Insira a data');
    }
    if (
      installmentType === INSTALLMENT_TYPES.PECUNIARY.FIXED_PER_PERIOD &&
      (!value || value == '0')
    ) {
      return toast.warn('Insira um valor');
    }

    let date = paymentDeadline && moment(paymentDeadline);

    const parcel = {
      date: date && formatDateToISO8601(date),
      value,
      maximumValue,
      minimumValue,
    };

    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        parcels: [...formState.values.parcels, parcel],
      },
    }));

    setIsAddingParcel(false);
  };

  const orderParcelsByDate = parcels => {
    return parcels.sort((a, b) => Date.parse(a.date) - Date.parse(b.date));
  };

  useEffect(() => {
    setFormState(formState => ({
      ...formState,
      values:
        selectedIndex !== null
          ? {
              ...pecuniary[selectedIndex.index],
              descriptionLocal: isMPDFT ? pecuniary[selectedIndex.index].description : description,
            }
          : { ...initialValues },
    }));
    selectedIndex && setInstallmentType(pecuniary[selectedIndex.index]?.installmentType);
  }, [selectedIndex]);

  const handleChangeCompany = (_event, newValue) => {
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        association: newValue,
        project: null,
      },
    }));
  };

  const handleChangeType = (_event, newValue) => {
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        type: newValue,
      },
    }));
  };

  const fetchAssociations = async (search, page) => {
    const params = {
      forward: false,
      includeAssociationsWithNullSituation: true,
      judicialDistrictId: judicialDistrictId,
      name: search,
      page,
      size: AutoCompleteConfig.PAGINATION_SIZE,
    };

    if (isMPDFT) {
      delete params.includeAssociationsWithNullSituation;
      params.situation = PARTNER_SITUATIONS.ACCREDITED;
      delete params.judicialDistrictId;
    }

    if (appliedInstituteFilterKey && isMPDFT) {
      params[appliedInstituteFilterKey] = true;
    }

    const result = await associationService.getAssociationsService(params);

    return {
      data: result.data.data,
      lastPage: result.data.lastPage,
    };
  };

  const fetchProjects = async (search, page) => {
    const params = {
      associationId: formState.values.association?.id,
      onlyActiveProjects: true,
      name: search,
      page,
      size: AutoCompleteConfig.PAGINATION_SIZE,
    };

    const result = await projectService.getProjectsService(params);

    return {
      data: result.data.data,
      lastPage: result.data.lastPage,
    };
  };

  const fetchTypes = async () => {
    const result = await agreementService.getPenaltyTypes();
    return {
      data: result.data.pecuniaryTypes,
    };
  };

  useEffect(() => {
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        descriptionLocal: description,
      },
    }));
  }, [description]);

  const handleChangeCurrency = event => {
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        [event.target.name]: getOnlyNumbers(event.target.value),
      },
    }));
  };

  const handleInputKeyPress = event => {
    if (event.key === ',' || event.key === '.') {
      event.preventDefault();
    }
  };

  const handleChange = event => {
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        [event.target.name]:
          event.target.type === 'checkbox' ? event.target.checked : event.target.value,
      },
      touched: {
        ...formState.touched,
        [event.target.name]: true,
      },
    }));
    if (event.target.name === 'descriptionLocal') {
      setFormState(formState => ({
        ...formState,
        values: {
          ...formState.values,
          description: event.target.value,
        },
      }));
    }
  };

  const handleClearFormStat = () => {
    setRow(null);
    if (selectedIndex !== null) {
      setDisabledActionsPenaltyTable(prevState => ({
        ...prevState,
        [selectedIndex.penaltiesIndex]: { ...prevState[selectedIndex.penaltiesIndex], edit: false },
      }));
      setSelectedIndex(null);
    }
    if (isMPDFT) {
      setFormState(formState => ({
        ...formState,
        values: {
          ...initialValues,
          description: '',
        },
      }));
    } else {
      setFormState(formState => ({
        ...formState,
        values: {
          ...initialValues,
        },
      }));
    }
  };

  useEffect(() => {
    if (row) {
      setFormState(formState => ({
        ...formState,
        values: { ...row, descriptionLocal: isMPDFT ? row.description : description },
      }));
    }
    enableInstallmentTypes &&
      setInstallmentType(row?.installmentType || INSTALLMENT_TYPES.PECUNIARY.FIXED_PER_PERIOD);
  }, [row, institutionSelected]);

  const handleSubmit = event => {
    event.preventDefault();
    const { paymentDeadline, descriptionLocal, parcels, association, type } = formState.values;

    if (parcels.length === 0) {
      toast.warn('É necessário adicionar ao menos uma parcela');
      return;
    }

    if (!validateParcels(parcels, installmentType)) {
      return;
    }

    if (enablePenaltyTypes && !type) {
      toast.warn('Selecione o tipo');
      return;
    }

    if (institutionType === 'final' && !association) {
      toast.warn('Selecione a instituição');
      return;
    }

    if (!isMPDFT && descriptionLocal === '') {
      toast.warn('Descreva como será cumprido');
      return;
    }

    if (selectedIndex !== null) {
      if (
        paymentDeadline &&
        pecuniary.find(value => value.paymentDeadline === paymentDeadline && value.value === value)
      ) {
        toast.warn('Data e valor já inseridos');
        return;
      }

      let newArray = [...pecuniary];
      let newPecuniary = formState.values;
      setDescription(newPecuniary.descriptionLocal);
      delete newPecuniary.descriptionLocal;
      newArray[selectedIndex.index] = newPecuniary;
      setPecuniary(newArray);
    } else {
      if (
        paymentDeadline &&
        pecuniary.find(value => value.paymentDeadline === paymentDeadline && value.value === value)
      ) {
        toast.warn('Data e valor já inseridos');
        return;
      }
      let newPecuniary = formState.values;
      setDescription(newPecuniary.descriptionLocal);
      delete newPecuniary.descriptionLocal;
      setPecuniary([...pecuniary, newPecuniary]);
    }

    handleClearFormStat();
  };

  const handleAddParcel = () => {
    const { paymentDeadline, value, numberOfParcels } = formState.values;

    if (!paymentDeadline) {
      toast.warn('Insira a data inicial');
      return;
    }
    if (!value || value == '0') {
      toast.warn('Insira um valor');
      return;
    }
    if (!numberOfParcels) {
      toast.warn('Insira a quantidade de parcelas');
      return;
    }

    const newParcels = [];
    let date = paymentDeadline && moment(paymentDeadline);

    for (let i = 0; i < numberOfParcels; i++) {
      newParcels.push({
        date: date && formatDateToISO8601(date.clone().add(i, 'months')),
        value: value,
      });
    }

    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        parcels: newParcels,
        paymentDeadline: '',
        value: 0,
      },
    }));
  };

  const handleChangeProject = (_event, newValue) => {
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        project: newValue,
      },
    }));
  };

  const handleParcelDateChange = (index, newDate, isInitial = false) => {
    const updatedParcels = formState.values.parcels.map((parcel, idx) =>
      idx === index
        ? isInitial
          ? { ...parcel, initialDate: newDate }
          : { ...parcel, date: newDate }
        : parcel,
    );
    setFormState({
      ...formState,
      values: {
        ...formState.values,
        parcels: updatedParcels,
      },
    });
  };

  const handleParcelValueChange = (index, event) => {
    const updatedParcels = formState.values.parcels.map((parcel, idx) =>
      idx === index
        ? { ...parcel, [event.target.name]: getOnlyNumbers(event.target.value) }
        : parcel,
    );
    setFormState({
      ...formState,
      values: {
        ...formState.values,
        parcels: updatedParcels,
      },
    });
  };

  const handleOpenDeleteParcelModalConfirm = parcel => {
    setOpenDeleteParcelModalConfirm(true);
    setParcelToDelete(parcel);
  };

  const handleCloseDeleteParcelModalConfirm = () => {
    setOpenDeleteParcelModalConfirm(false);
    setParcelToDelete(null);
  };

  const deleteParcel = () => {
    const parcel = parcelToDelete;
    const index = formState.values.parcels.findIndex(item => item.date === parcel.date);
    if (index !== -1) {
      const newParcels = formState.values.parcels.filter(
        (value, _index, _arr) => value.date !== parcel.date,
      );
      setFormState(formState => ({
        ...formState,
        values: {
          ...formState.values,
          parcels: newParcels,
        },
      }));
    }
  };

  const handleToRead = () => {
    handleClearFormStat();
  };

  return (
    <Box className={classes.container}>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2} className={classes.containerForm}>
          <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
            {enablePenaltyTypes && (
              <LazyAutocomplete
                autoHighlight
                onChange={handleChangeType}
                value={formState.values.type}
                fetchOptions={fetchTypes}
                enablePagination
                renderInput={params => (
                  <InputConsensus
                    {...params}
                    label="Tipo*"
                    variant="standard"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {params.InputProps.loading ? (
                            <CircularProgress color="primary" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
          </Grid>
          <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
            {institutionType === 'final' && (
              <LazyAutocomplete
                autoHighlight
                onChange={handleChangeCompany}
                value={formState.values.association}
                getOptionLabel={option =>
                  option?.name
                    ? `${option.name}${
                        option.exclusiveProjects ? ' (exclusivamente projetos)' : ''
                      }`
                    : ''
                }
                enablePagination
                refreshKey={isMPDFT ? (appliedInstituteFilterKey ?? 'none') : undefined}
                fetchOptions={fetchAssociations}
                renderInput={params => (
                  <InputConsensus
                    {...params}
                    label="Instituição*"
                    variant="standard"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {params.InputProps.loading ? (
                            <CircularProgress color="primary" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
          </Grid>
          <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
            {institutionType === 'final' && formState.values.association?.id && (
              <LazyAutocomplete
                value={formState.values.project}
                getOptionLabel={option => option?.name || ''}
                onChange={handleChangeProject}
                fetchOptions={fetchProjects}
                enablePagination
                refreshKey={formState.values.association?.id}
                renderInput={params => (
                  <InputConsensus
                    {...params}
                    name="project"
                    label="Projeto"
                    variant="standard"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {params.InputProps.loading ? (
                            <CircularProgress color="primary" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
          </Grid>
          <Grid
            container
            spacing={2}
            className={classes.containerParcels}
            size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
            <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography align="left" variant="h5" className={classes.form}>
                  Parcelas
                </Typography>
                {formState.values.parcels.length > 0 &&
                  isMPDFT &&
                  !isAddingParcel &&
                  [
                    INSTALLMENT_TYPES.PECUNIARY.FIXED_PER_PERIOD,
                    INSTALLMENT_TYPES.PECUNIARY.VARIABLE_PER_PERIOD,
                  ].includes(installmentType) && (
                    <Tooltip title="Adicionar parcela" arrow placement="top">
                      <IconButton onClick={handleToggleAddParcel}>
                        <AddIcon style={{ color: colors.primary }} />
                      </IconButton>
                    </Tooltip>
                  )}
              </Box>
            </Grid>

            {isAddingParcel && (
              <>
                <Grid size={{ xl: 3, lg: 3, md: 3, sm: 3, xs: 12 }}>
                  <KeyboardDatePicker
                    variant="inline"
                    name="newParcelDate"
                    label="Data"
                    value={newParcel.paymentDeadline || null}
                    onChange={newDate =>
                      setNewParcel({
                        ...newParcel,
                        paymentDeadline: newDate,
                      })
                    }
                  />
                </Grid>
                {installmentType === INSTALLMENT_TYPES.PECUNIARY.FIXED_PER_PERIOD && (
                  <Grid size={{ xl: 4, lg: 4, md: 4, sm: 4, xs: 12 }}>
                    <CurrencyTextField
                      label="Valor *"
                      name="newParcelValue"
                      onChange={e =>
                        setNewParcel({ ...newParcel, value: getOnlyNumbers(e.target.value) })
                      }
                      value={newParcel.value}
                      fullWidth
                    />
                  </Grid>
                )}
                {installmentType === INSTALLMENT_TYPES.PECUNIARY.VARIABLE_PER_PERIOD && (
                  <>
                    <Grid size={{ xl: 3, lg: 3, md: 3, sm: 3, xs: 12 }}>
                      <CurrencyTextField
                        label="Valor Mínimo"
                        name="newParcelMinimumValue"
                        onChange={e =>
                          setNewParcel({
                            ...newParcel,
                            minimumValue: getOnlyNumbers(e.target.value),
                          })
                        }
                        value={newParcel.minimumValue}
                        allowNullValue={true}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ xl: 3, lg: 3, md: 3, sm: 3, xs: 12 }}>
                      <CurrencyTextField
                        label="Valor Máximo"
                        name="newParcelMaximumValue"
                        onChange={e =>
                          setNewParcel({
                            ...newParcel,
                            maximumValue: getOnlyNumbers(e.target.value),
                          })
                        }
                        value={newParcel.maximumValue}
                        allowNullValue={true}
                        fullWidth
                      />
                    </Grid>
                  </>
                )}
                <Grid
                  style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '30px' }}
                  size={{ xl: 2, lg: 2, md: 2, sm: 2, xs: 12 }}>
                  <Tooltip title="Adicionar parcela" arrow placement="top">
                    <IconButton onClick={handleAddNewParcel}>
                      <AddIcon style={{ color: colors.primary }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Cancelar" arrow placement="top">
                    <IconButton onClick={handleToggleAddParcel} color="secondary">
                      <ClearIcon style={{ color: '#e1296a' }} />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </>
            )}

            {formState.values.parcels.length === 0 && enableInstallmentTypes && (
              <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
                <InstallmentTypeRadioGroup
                  installmentType={installmentType}
                  setInstallmentType={setInstallmentType}
                  context={context}
                />
              </Grid>
            )}

            {enableInstallmentTypes ? (
              <>
                {installmentType === INSTALLMENT_TYPES.PECUNIARY.FIXED_PER_PERIOD && (
                  <InstallmentFixedPerPeriod
                    formState={formState}
                    setFormState={setFormState}
                    handleChange={handleChange}
                    handleChangeValue={handleChangeCurrency}
                    handleInputKeyPress={handleInputKeyPress}
                    handleOpenDeleteParcelModalConfirm={handleOpenDeleteParcelModalConfirm}
                    handleParcelValueChange={handleParcelValueChange}
                    handleParcelDateChange={handleParcelDateChange}
                    selectedIndex={selectedIndex}
                    context={context}
                  />
                )}
                {installmentType === INSTALLMENT_TYPES.PECUNIARY.VARIABLE_PER_PERIOD && (
                  <InstallmentVariablePerPeriod
                    formState={formState}
                    setFormState={setFormState}
                    handleChange={handleChange}
                    handleChangeCurrency={handleChangeCurrency}
                    handleInputKeyPress={handleInputKeyPress}
                    handleOpenDeleteParcelModalConfirm={handleOpenDeleteParcelModalConfirm}
                    handleParcelValueChange={handleParcelValueChange}
                    handleParcelDateChange={handleParcelDateChange}
                    selectedIndex={selectedIndex}
                    context={context}
                  />
                )}
                {installmentType === INSTALLMENT_TYPES.PECUNIARY.PAID_BY_SPECIFIC_DATE && (
                  <InstallmentPaidBySpecificDate
                    formState={formState}
                    setFormState={setFormState}
                    handleChangeCurrency={handleChangeCurrency}
                    handleOpenDeleteParcelModalConfirm={handleOpenDeleteParcelModalConfirm}
                    handleParcelDateChange={handleParcelDateChange}
                    handleParcelValueChange={handleParcelValueChange}
                    selectedIndex={selectedIndex}
                    context={context}
                  />
                )}
              </>
            ) : (
              <>
                {formState.values.parcels.length === 0 && (
                  <>
                    <Grid size={{ xl: 5, lg: 5, md: 5, sm: 5, xs: 12 }}>
                      <KeyboardDatePicker
                        variant="inline"
                        name="paymentDeadline"
                        label={'Data inicial*'}
                        value={formState.values.paymentDeadline || null}
                        minDate={!selectedIndex ? new Date() : undefined}
                        onChange={newDate =>
                          setFormState(formState => ({
                            ...formState,
                            values: {
                              ...formState.values,
                              paymentDeadline: newDate,
                            },
                          }))
                        }
                      />
                    </Grid>
                    <Grid size={{ xl: 5, lg: 5, md: 5, sm: 5, xs: 12 }}>
                      <CurrencyTextField
                        label="Valor por parcela*"
                        name="value"
                        onChange={handleChangeCurrency}
                        value={formState.values.value || 0}
                        fullWidth
                      />
                    </Grid>
                  </>
                )}
                {formState.values.parcels && formState.values.parcels.length === 0 && (
                  <>
                    <Grid size={{ xl: 2, lg: 2, md: 2, sm: 2, xs: 12 }}>
                      <InputConsensus
                        label="Quantidade de parcelas*"
                        name="numberOfParcels"
                        variant="standard"
                        type="number"
                        inputProps={{
                          min: 1,
                        }}
                        onChange={handleChange}
                        onKeyPress={handleInputKeyPress}
                        value={formState.values.numberOfParcels || 1}
                        fullWidth
                      />
                    </Grid>
                    <Grid
                      style={{ display: 'flex', justifyContent: 'flex-end' }}
                      size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
                      <ButtonConsensus
                        title="Gerar Parcelas"
                        backgroundColor={colors.primary}
                        hovercolor={colors.primary}
                        onClick={() => handleAddParcel()}
                        size="medium"
                      />
                    </Grid>
                  </>
                )}
              </>
            )}
          </Grid>
          {!enableInstallmentTypes && (
            <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
              <TableContainer component={Paper}>
                <Table className={classes.table} size="small" aria-label="a dense table">
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">Data</TableCell>
                      <TableCell align="center">Valor</TableCell>
                      <TableCell align="center" width={150}>
                        <Box display="flex" position="relative" justifyContent="center">
                          Ações
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orderParcelsByDate(formState.values.parcels).map((item, index) => (
                      <TableRow key={index} hover>
                        <TableCell component="th" scope="row" align="center">
                          <KeyboardDatePicker
                            variant="inline"
                            name="paymentDeadline"
                            value={item.date || null}
                            onChange={newDate => handleParcelDateChange(index, newDate)}
                          />
                        </TableCell>
                        <TableCell component="th" scope="row" align="center">
                          <CurrencyTextField
                            name="value"
                            onKeyPress={handleInputKeyPress}
                            onChange={e => handleParcelValueChange(index, e)}
                            value={item.value / 100 || 0}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => handleOpenDeleteParcelModalConfirm(item)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TableCell align="left" colSpan={3} style={{ fontWeight: 'bold' }}>
                        Total: {currencyFormat(parcelsSum(formState.values.parcels))}
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            </Grid>
          )}
          <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
            <TextareaAutosize
              style={{ width: '100%', padding: 5, resize: 'none' }}
              maxRows={4}
              minRows={4}
              aria-label="maximum height"
              placeholder={isMPDFT ? 'Observação' : 'Descrever como será cumprido*'}
              onChange={handleChange}
              name="descriptionLocal"
              value={isMPDFT ? formState.values.description : formState.values.descriptionLocal}
            />
          </Grid>
          <Grid
            style={{ display: 'flex', justifyContent: 'flex-end' }}
            size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
            <ButtonConsensus
              title={`${selectedIndex !== null ? 'Alterar' : 'Incluir'} ${
                isMPDFT ? 'Medida' : 'Penalidade'
              }`}
              backgroundColor={colors.primary}
              hovercolor={colors.primary}
              type="submit"
              size="medium"
            />
            <ButtonConsensus
              title={`${selectedIndex !== null ? 'Cancelar' : 'Limpar'}`}
              backgroundColor={colors.primary}
              hovercolor={colors.primary}
              onClick={() => handleToRead()}
              size="medium"
              style={{ marginLeft: '-6px' }}
            />
          </Grid>
        </Grid>
      </form>
      {openDeleteParcelModalConfirm && (
        <ModalConfirm
          open={openDeleteParcelModalConfirm}
          handleClose={handleCloseDeleteParcelModalConfirm}
          onClick={deleteParcel}
          title="Excluir parcela"
        />
      )}
    </Box>
  );
};

export default PecuniaryPenalties;
